/**
 * Storage Service Contract
 * Handles browser localStorage operations and data persistence
 */

import type { Metadata, ImageContext } from './metadataGenerator'

export interface BatchImage {
  id: string
  file?: File
  metadata?: Metadata
}

export interface Batch {
  id: string
  name: string
  createdAt: Date
  updatedAt?: Date
  images?: BatchImage[]
  context?: ImageContext
  size?: number
}

export interface StorageInfo {
  used: number // Bytes used
  available: number // Bytes available
  total: number // Total quota
  percentage: number // Usage percentage
  itemCount: number // Number of stored items
}

export interface StorageOptions {
  compress?: boolean // Use compression for large data
  backup?: boolean // Create backup before write
  validate?: boolean // Validate data before storage
}

export interface BackupInfo {
  id: string
  createdAt: Date
  size: number
  itemCount: number
  description?: string
}

export interface QueryOptions {
  sortBy?: 'createdAt' | 'updatedAt' | 'name' | 'size'
  sortOrder?: 'asc' | 'desc'
  limit?: number
  offset?: number
  filter?: (item: any) => boolean
}

/**
 * Storage Service Interface
 * All implementations must adhere to this contract
 */
export interface StorageService {
  /**
   * Saves a batch to storage
   * @param batch - Batch object to save
   * @param options - Storage options
   * @returns Promise resolving when save complete
   * @throws StorageError if save fails
   */
  saveBatch(batch: Batch, options?: StorageOptions): Promise<void>

  /**
   * Loads a batch from storage
   * @param id - Batch identifier
   * @returns Promise resolving to Batch object or null if not found
   * @throws StorageError if load fails
   */
  loadBatch(id: string): Promise<Batch | null>

  /**
   * Lists all stored batches with optional filtering/sorting
   * @param options - Query options for filtering and sorting
   * @returns Promise resolving to array of Batch objects
   */
  listBatches(options?: QueryOptions): Promise<Batch[]>

  /**
   * Deletes a batch from storage
   * @param id - Batch identifier
   * @returns Promise resolving when deletion complete
   * @throws StorageError if deletion fails
   */
  deleteBatch(id: string): Promise<void>

  /**
   * Gets current storage usage information
   * @returns Promise resolving to storage info
   */
  getStorageUsage(): Promise<StorageInfo>

  /**
   * Checks if storage has sufficient space
   * @param requiredBytes - Bytes needed for operation
   * @returns Promise resolving to availability check
   */
  hasSpace(requiredBytes: number): Promise<boolean>

  /**
   * Cleans up old or unused data
   * @param maxAge - Maximum age in days for data retention
   * @param forceCleanup - Force cleanup even if space available
   * @returns Promise resolving to cleanup summary
   */
  cleanup(maxAge?: number, forceCleanup?: boolean): Promise<CleanupSummary>

  /**
   * Creates a backup of all data
   * @param description - Optional backup description
   * @returns Promise resolving to backup info
   */
  createBackup(description?: string): Promise<BackupInfo>

  /**
   * Restores data from a backup
   * @param backupId - Backup identifier
   * @returns Promise resolving when restore complete
   */
  restoreBackup(backupId: string): Promise<void>

  /**
   * Lists available backups
   * @returns Promise resolving to array of backup info
   */
  listBackups(): Promise<BackupInfo[]>

  /**
   * Exports all data as JSON
   * @returns Promise resolving to JSON string
   */
  exportData(): Promise<string>

  /**
   * Imports data from JSON
   * @param jsonData - JSON string to import
   * @param merge - Whether to merge with existing data
   * @returns Promise resolving when import complete
   */
  importData(jsonData: string, merge?: boolean): Promise<void>

  /**
   * Validates storage integrity
   * @returns Promise resolving to validation result
   */
  validateIntegrity(): Promise<ValidationResult>

  /**
   * Gets metadata about a stored item
   * @param key - Storage key
   * @returns Promise resolving to item metadata
   */
  getItemMetadata(key: string): Promise<ItemMetadata | null>

  /**
   * Monitors storage changes
   * @param callback - Function called on storage changes
   * @returns Unsubscribe function
   */
  onStorageChange(callback: StorageChangeCallback): () => void
}

export interface CleanupSummary {
  itemsDeleted: number
  bytesFreed: number
  duration: number
  errors: string[]
}

export interface ItemMetadata {
  key: string
  size: number
  createdAt: Date
  updatedAt: Date
  type: string
  compressed: boolean
}

export interface ValidationResult {
  isValid: boolean
  errors: ValidationError[]
  warnings: ValidationWarning[]
  corruptedKeys: string[]
}

export interface ValidationError {
  key: string
  error: string
  details?: any
}

export interface ValidationWarning {
  key: string
  warning: string
  suggestion?: string
}

export type StorageChangeCallback = (change: StorageChange) => void

export interface StorageChange {
  type: 'created' | 'updated' | 'deleted'
  key: string
  oldValue?: any
  newValue?: any
  timestamp: Date
}

/**
 * Error types thrown by StorageService implementations
 */
export class StorageError extends Error {
  constructor(
    message: string,
    public code: string,
    public operation?: string,
    public details?: Record<string, any>,
  ) {
    super(message)
    this.name = 'StorageError'
  }
}

export class StorageQuotaError extends StorageError {
  constructor(
    message: string,
    public requiredBytes: number,
    public availableBytes: number,
  ) {
    super(message, 'QUOTA_EXCEEDED', 'write')
    this.name = 'StorageQuotaError'
  }
}

/**
 * Storage error codes
 */
export const STORAGE_CODES = {
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  ITEM_NOT_FOUND: 'ITEM_NOT_FOUND',
  INVALID_DATA: 'INVALID_DATA',
  SERIALIZATION_ERROR: 'SERIALIZATION_ERROR',
  CORRUPTION_DETECTED: 'CORRUPTION_DETECTED',
  ACCESS_DENIED: 'ACCESS_DENIED',
  STORAGE_UNAVAILABLE: 'STORAGE_UNAVAILABLE',
  BACKUP_FAILED: 'BACKUP_FAILED',
  RESTORE_FAILED: 'RESTORE_FAILED',
} as const

/**
 * Storage key patterns used by the application
 */
export const STORAGE_KEYS = {
  BATCH_PREFIX: 'image-tagger:batch:',
  APP_STATE: 'image-tagger:app-state',
  SETTINGS: 'image-tagger:settings',
  BACKUP_PREFIX: 'image-tagger:backup:',
  METADATA_PREFIX: 'image-tagger:meta:',
} as const

/**
 * Default storage limits and configurations
 */
export const STORAGE_CONFIG = {
  MAX_ITEM_SIZE: 5 * 1024 * 1024, // 5MB per item
  MAX_TOTAL_SIZE: 50 * 1024 * 1024, // 50MB total
  DEFAULT_CLEANUP_AGE: 30, // 30 days
  COMPRESSION_THRESHOLD: 1024 * 1024, // 1MB
  BACKUP_RETENTION: 5, // Keep 5 backups max
  VALIDATION_INTERVAL: 24 * 60 * 60 * 1000, // 24 hours
} as const

/**
 * Data compression utilities interface
 */
export interface CompressionUtils {
  compress(data: string): Promise<string>
  decompress(data: string): Promise<string>
  canCompress(data: string): boolean
  getCompressionRatio(original: string, compressed: string): number
}
